import { useLocation } from 'react-router-dom'
import { ReactNode, useMemo } from 'react'
import { BrandModeProvider, type BrandMode } from '../contexts/BrandModeContext'

/**
 * Route-to-BrandMode mapping (locked design direction).
 *
 * Mode A (Stealth Luxury): Home, Pricing, Docs landing, Company/legal pages
 * Mode B (Technical Museum): Constellation, Technology, Matriz, Studio, Research,
 *   Playground, Docs deep (SDK/tutorials/API)
 * Mode C (Dream-Editorial): About, Dream, Blog, Brand, Timeline, Experience
 *
 * Default: A (most pages are Stealth Luxury)
 *
 * Rule: per-page only. No mixed-mode sections.
 */
const MODE_B_PREFIXES = [
  '/constellation',
  '/technology',
  '/matriz',
  '/studio',
  '/research',
  '/playground',
  '/chat',
  '/vision',
  '/constellation-preview',
  '/solutions/governance',
  '/solutions/agents',
  '/solutions/insights',
  '/products/',
  // Docs deep pages use Mode B (Technical Museum density)
  // Docs landing (/docs exact) falls through to default A
  '/docs/sdk',
  '/docs/tutorials',
  '/docs/api',
]

const MODE_C_PREFIXES = [
  '/about',
  '/dream-experience',
  '/blog',
  '/brand',
  '/timeline',
  '/experience',
  '/use-cases',
  '/enterprise',
  '/backgrounds',
  '/solutions/creative',
]

function getModeForPath(pathname: string): BrandMode {
  for (const prefix of MODE_C_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(prefix)) return 'C'
  }
  for (const prefix of MODE_B_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(prefix)) return 'B'
  }
  return 'A'
}

/**
 * BrandModeRouter — wraps children with BrandModeProvider using
 * the mode derived from the current route pathname.
 *
 * Place inside Router context, outside Routes.
 */
export default function BrandModeRouter({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const mode = useMemo(() => getModeForPath(pathname), [pathname])

  return <BrandModeProvider mode={mode}>{children}</BrandModeProvider>
}
